import Marionette from "backbone.marionette";
import Radio from "backbone.radio";
import ChartModel from "../../../models/chart-reviews/chart";
import TabCollection from "../../../collections/tabs";
import GenericModel from "../../../models/generic";
import ChartView from "./chart";
import DRGListView from "../drg-list";
import DRGAddView from "../drg-add";

// setup data related imports
import MemberModel from "../../../models/chart-reviews/member";
import MemberEligibilityCollection from "../../../collections/chart-reviews/member-eligibility";
import UserCollection from "../../../collections/chart-reviews/reviewers/selectable";
import NoteCollection from "../../../collections/notes";
import StringKeyValueCollection from "../../../collections/key-string-value";
import NumericKeyValueCollection from "../../../collections/key-numeric-value";
import DiagnosisAttributesCollection from "../../../collections/chart-reviews/diag-attributes";

import { createMemberCategories } from "../../../utils/chart-reviews/member-categories";

// common templates and environment
import templates from "../../../utils/templates";
import environment from "../../../common/environment";

const channel = Radio.channel("global");

const DEFAULTS = {
  title: "Chart Review",
  className: "row",
  template: templates.find("layout", "generic_tabbed_view"),
};

// tab configuration
const VIEW_CONFIG = [
  { id: "chart-edit", label: "Chart", active: false, view: ChartView },
  { id: "drg-list", label: "Current Diags", active: false, view: DRGListView },
  { id: "drg-add", label: "Add Diags", active: false, view: DRGAddView },
];

class View extends Marionette.LayoutView {
  constructor(options = {}) {
    _.defaults(options, DEFAULTS);
    super(options);
  }

  ui() {
    return {
      tabSwitch: ".help-block a",
      btnTabSwitch: "button.tab-switch",
    };
  }

  events() {
    return {
      "shown.bs.tab": "_onTabShown",
      "click @ui.tabSwitch": "_onTabSwitch",
      "click @ui.btnTabSwitch": "_onTabSwitch",
    };
  }

  childEvents() {
    return {
      "modal:close": "_hide",
    };
  }

  initialize() {
    // create tab collection
    this.collection = new TabCollection(VIEW_CONFIG);

    // create empty API chart model
    this.chartModel = new ChartModel();

    // copy original model to be used by children views
    this.chartFormModel = (this.model && this.model.clone()) || null;
  }

  onBeforeRender() {
    // determine the active tab
    const status = this.options.model.get("statusValue");
    let tabId = "chart-edit";
    if ("OPEN" === status) {
      tabId = "drg-add";
    }
    this.collection.get(tabId).set({ active: true });

    // model needed by layout view
    this.model = new GenericModel({
      tabs: this.collection.toJSON(),
    });

    // add regions to layout
    this.collection.each((tab) => {
      this.addRegion(tab.getId(), `#tab-${tab.id}`);
    });
  }

  // retrieve setup data, and fetch chart details on success
  onBeforeShow() {
    $.when(
      $.ajax(environment.getEndpoint("chart-reviews:projects")),
      $.ajax(environment.getEndpoint("reviewers")),
      $.ajax(environment.getEndpoint("chart-reviews:retrievalStatuses")),
      $.ajax(environment.getEndpoint("chart-reviews:statuses")),
      $.ajax(environment.getEndpoint("chart-reviews:chartAttributes")),
      $.ajax(environment.getEndpoint("chart-reviews:diagnosis-attributes"))
    ).then(
      // on success
      (
        projects,
        reviewers,
        retrievalStatuses,
        statuses,
        chartAttributes,
        diagnosisAttributes
      ) => {
        // create diagnosis attributes collection
        const diagnosisAttributesColl = new DiagnosisAttributesCollection(
          diagnosisAttributes[0]
        );
        // create key/value list of diagnosis attributes to be used in UI
        const diagnosisAttributesList = diagnosisAttributesColl.map((attr) => {
          return {
            key: `${attr.getId()}`,
            value: `${attr.getCode()} - ${attr.getDescription()} (${attr.getStatusValue()})`,
          };
        });

        // localize results
        this.setupData = {
          projectIdList: new NumericKeyValueCollection(projects[0]),
          reviewersList: new UserCollection(reviewers[0]),
          retrievalStatusList: new StringKeyValueCollection(
            retrievalStatuses[0]
          ),
          statusList: new StringKeyValueCollection(statuses[0]),
          chartAttributeList: new NumericKeyValueCollection(chartAttributes[0]),
          diagnosisAttributesList,
        };

        // fetch project
        this.chartFormModel.promise = this.chartFormModel.fetch();
        this.chartFormModel.promise.done(this._onChartModelLoaded.bind(this));
        this.chartFormModel.promise.fail(
          this._onChartModelLoadFailure.bind(this)
        );
      },
      // on failure
      () => {
        // show error
        channel.trigger("status:show", {
          autoDismiss: false,
          type: "danger",
          message:
            "We were unable to retrieve all the data necessary for chart editing. Please try again later.",
        });
      }
    );
  }

  _onChartModelLoaded(model) {
    // update API chart model
    this.chartModel.set({
      chartAttributes: model.chartAttributes,
      customerRefNumber: model.customerRefNumber,
      id: model.id,
      member: model.member.id,
      projectId: model.projectId,
      provider: model.provider.id,
      providerGroupNumber: model.providerGroupNumber,
      retrievalStatus: model.retrievalStatus,
      retrievalStatusNAReason: model.retrievalStatusNAReason,
      reviewers: model.reviewers,
      statusValue: model.statusValue,
      receivedDate: model.receivedDate,
      reviewedDate: model.reviewedDate,
      closedDate: model.closedDate,
      customerLocation: model.customerLocation,
      userField1: model.userField1,
      userField2: model.userField2,
      userField3: model.userField3,
    });

    // create an empty member model
    const member = new MemberModel();

    // listen once for a change to it
    member.once("change", () => {
      // configure an instance of the member eligibility model
      const EligibilityModel = MemberEligibilityCollection.extend({
        // set its URL to point to the current member
        url: environment.getEndpoint("member:eligibility", { id: member.id }),
        // set it to fetch on instantiation
        initialize() {
          // fetch and return a promise
          this.promise = this.fetch();
        },
      });
      // attach instance of eligibility model to member model
      member.setEligibility(new EligibilityModel());
    });

    // set the new member model data with the non-model values in the
    // chart's member property. This causes the eligibility data to be
    // fetched.
    member.set(model.member);

    // FIX NOTE AUTHORS
    // converts author ID to User for each note
    const notes = [];
    const userColl = this.chartFormModel.get("users");

    // iterate over the raw notes array
    _.each(model.notes, (note) => {
      // find user by id (note.author)
      const user = userColl.get(note.author);
      // update author ID with actual user object when found
      note.author = user ? user.toJSON() : null;
      // remove "checked" prop, which is not supported by note model
      delete note.author.checked;
      // push note into notes array
      notes.push(note);
    });
    // create note collection from notes array
    const noteColl = new NoteCollection(notes);

    // SET ACTIVE SELECTIONS
    let data = null;

    // set active status
    this.setupData.statusList.map((item) => {
      if (model.statusValue === item.getKey()) {
        item.setChecked(true);
      }
      return item;
    });

    // set active project
    this.setupData.projectIdList.map((item) => {
      if (model.projectId === item.getKey()) {
        item.setChecked(true);
      }
      return item;
    });

    // set active retrieval status
    this.setupData.retrievalStatusList.map((item) => {
      if (model.retrievalStatus === item.getKey()) {
        item.setChecked(true);
      }
      return item;
    });

    data = model.chartAttributes;
    this.setupData.chartAttributeList.map((item) => {
      if (_.contains(data, item.getKey())) {
        item.setChecked(true);
      }
      return item;
    });

    data = model.reviewers;
    this.setupData.reviewersList.map((item) => {
      if (_.contains(data, item.getId())) {
        item.setChecked(true);
      }
      return item;
    });

    const modelData = Object.assign(
      this.setupData,
      // add member instance created above
      {
        selectedMember: member,
        member: model.member.id,
      },
      // add providers as loaded by model
      {
        selectedProvider: model.provider,
        provider: model.provider.id,
      },
      // add note collection created above
      {
        notes: noteColl,
      }
    );

    // lastly, fetch HCC expected categories
    $.when(
      $.ajax({
        url: environment.getEndpoint("member:expected-categories", {
          id: model.member.id,
        }),
        data: {
          benefitYear: ["processingYear", "processingYear"],
          asOfMonth: "latest",
          length: 100,
          start: 0,
        },
      })
    ).then(
      // on success
      (categories) => {
        modelData.memberCategories = createMemberCategories(categories);

        this.chartFormModel.set(modelData);

        this._renderRegions();
      },
      // on failure
      () => {
        // close this modal
        this._hide();

        // show error
        channel.trigger("status:show", {
          autoDismiss: false,
          type: "danger",
          message:
            "We were unable to retrieve member categories data. Please try again later.",
        });
      }
    );
  }

  _onChartModelLoadFailure() {
    // show error
    channel.trigger("status:show", {
      autoDismiss: false,
      type: "danger",
      message:
        "We were unable to retrieve the chart details. Please try again later.",
    });
  }

  _renderRegions() {
    // add views to regions
    this.collection.each((tab) => {
      // extract view and view model from config
      const View = tab.getView();
      const ViewModel = tab.getViewModel();

      // does config define a view?
      if (View) {
        // does config define a view model?
        let view;
        if (ViewModel) {
          // configure view with view model
          view = new View({
            model: new ViewModel(),
            apiModel: this.chartModel,
            title: `${this.options.title}: ${tab.getLabel()}`,
            active: tab.getActive(),
          });
        } else {
          // configure view with passed in model
          view = new View({
            model: this.chartFormModel,
            apiModel: this.chartModel,
            title: `${this.options.title}: ${tab.getLabel()}`,
            active: tab.getActive(),
          });
        }

        // render the view
        this[tab.getId()].show(view);
      }
    });
  }

  _onTabShown(e) {
    channel.trigger("tab-activated", $(e.target).attr("aria-controls"));
  }

  _onTabSwitch(e) {
    e.preventDefault();

    const targetEl = $(e.currentTarget).data("toggle");
    const tab = this.$(`a[href='${targetEl}']`);
    if (tab) {
      // enable tab via bootstrap API
      tab.tab("show");
    }
  }

  _hide() {
    // notify parent layout of this change
    this.triggerMethod("modal:close");
  }
}

export { View as default };
